/* eslint-disable @typescript-eslint/no-explicit-any */

import { dayjs } from 'util/shared/date';

export enum OiTab {
  DAILY_OI = 'daily',
  INTRADAY = 'intraday',
}

export const OI_TAB_LABELS = new Map([
  [OiTab.DAILY_OI, 'Daily Open Interest'],
  [OiTab.INTRADAY, 'Intraday OI + Gamma'],
]);

export enum ProcessingState {
  LOADING = 'loading',
  FAILED_FETCH = 'failed_fetch',
  DONE = 'done',
}

export const PRICE_BOUNDS = [
  0.001, 0.002, 0.003, 0.004, 0.005, 0.01, 0.02, 0.03, 0.04,
].reverse(); // reverse to ensure that a higher zoom value on the slider leads to a higher zoom,
// instead of a lower val meaning higher zoom

export const STRIKE_ZOOM_BOUNDS = [
  0, 0.2, 0.35, 0.5, 0.65, 0.8, 0.9, 0.935, 0.97,
];

export type PriceCandleWithDateTime = {
  datetime: dayjs.Dayjs;
} & PriceCandle;

export type PriceCandle = {
  time: number; // in seconds
  open: number;
  high: number;
  low: number;
  close: number;
};

export type TwelveSeriesSymbolRsp = {
  values: PriceCandle[];
  meta: { exchange_timezone: string };
};

export type TwelveSeriesRsp = Record<string, TwelveSeriesSymbolRsp>;

export type TraceSettings = {
  zoom?: number;
  posColor?: string | null;
  negColor?: string | null;
  posStrikeColor?: string | null;
  negStrikeColor?: string | null;
  contourLineColor?: string | null;
  putColor?: string | null;
  callColor?: string | null;
  hiroColor?: string | null;
  upCandleFill?: string | null;
  downCandleFill?: string | null;
  upCandleLineColor?: string | null;
  downCandleLineColor?: string | null;
  candleDuration?: number;
  selectedLense?: TraceLense;
  hideLevels?: boolean;
  scaleRange?: OIScaleRange;
  showGexZeroDte?: boolean;
  hiroSyms?: Map<string, string>;
  strikeBarType?: TraceStrikeBarType;
  hideColorScale?: boolean;
  hideContourLines?: boolean;
  statsLookbackDays?: number;
  useWhiteMode?: boolean;
  strikeBarsTrackerDisabled?: boolean;
  strikeBarsHistoryDisabled?: boolean;
  customColorsPerLense?: any;
  strikeZoom?: number;
};

export enum TraceLense {
  DELTA_END_DIFF = 0,
  GAMMA = 1,
  GAMMA_DIRECTIONAL = 2,
  GAMMA_COLOR = 3,
  DELTA = 4,
  DELTA_DIRECTIONAL = 5,
  DELTA_CHARM_DIRECTIONAL = 6,
}

export enum OIEntity {
  CUST = 'cust',
  PROCUST = 'procust',
  MARKET_MAKER = 'mm',
  FIRM = 'firm',
  BD = 'bd',
}
export type OIEntityKey = keyof OIEntity;
export type OIEntityValue = OIEntity[OIEntityKey];

export enum OIScaleRange {
  AUTO = 'auto',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum TraceStrikeBarType {
  GAMMA = 'gamma',
  OI = 'oi',
  OI_NET = 'oi_net',
  NONE = 'none',
}

// TODO
export const StrikeBarDataType = new Map([
  [TraceStrikeBarType.GAMMA, 'gamma'],
  [TraceStrikeBarType.OI, 'oi'],
  [TraceStrikeBarType.OI_NET, 'oi'],
  [TraceStrikeBarType.NONE, null],
]);

export type ZoomData = {
  x?: string[];
  y?: number[];
  y2?: number[];
};

export enum TraceGreek {
  Gamma = 'gamma',
  Delta = 'delta',
}

export type TraceContourData = {
  chartData: {
    x: number[];
    y: number[];
    z: number[];
  };
  firstChartTimestamp: number;
  lastChartTimestamp: number;
  latestTimestamp: number;
};

export type StrikeBarsDailyTrackerEntry = {
  dailyMin?: number;
  dailyMax?: number;
  last?: number;
  thirtyMin?: number;
  hour?: number;
};

export type StrikeBarsDailyTracker = {
  entriesPerTrace: Map<string, Map<number, StrikeBarsDailyTrackerEntry>>; // traceName => map<strike, entry>
  type: TraceStrikeBarType;
  latestTimestamp: number;
  zeroDte: boolean;
};

export type StrikeBarsData = {
  latestTimestamp: number;
  trackerData: Plotly.PlotData[];
  chartData: Plotly.PlotData[];
  annotations: Plotly.PlotData[];
  range: number[] | undefined;
};

export interface IntradayStats {
  delta: { [key: string]: Delta };
  charm: { [key: string]: Charm };
  rel_delta: { [key: string]: RelDelta };
  gamma: { [key: string]: Gamma };
  color: { [key: string]: Color };
  rel_gamma: { [key: string]: RelGamma };
  gamma_vol: { [key: string]: GammaVol };
  strike_gex: { [key: string]: StrikeGex };
  strike_oi: { [key: string]: StrikeOi };
}

export interface Charm {
  bd: CharmBd;
  cust: CharmBd;
  firm: CharmBd;
  mm: CharmBd;
  procust: CharmBd;
}

export interface CharmBd {
  charm_pos: CharmNeg;
  charm_neg: CharmNeg;
}

export interface CharmNeg {
  percentiles: { [key: string]: number | null };
  std: number | null;
  mean: number | null;
  max: number | null;
  min: number | null;
}

export interface Color {
  bd: ColorBd;
  cust: ColorBd;
  firm: ColorBd;
  mm: ColorBd;
  procust: ColorBd;
}

export interface ColorBd {
  color_pos: CharmNeg;
  color_neg: CharmNeg;
}

export interface Delta {
  bd: DeltaBd;
  cust: DeltaBd;
  firm: DeltaBd;
  mm: DeltaBd;
  procust: DeltaBd;
}

export interface DeltaBd {
  delta_pos: CharmNeg;
  delta_neg: CharmNeg;
}

export interface Gamma {
  bd: GammaBd;
  cust: GammaBd;
  firm: GammaBd;
  mm: GammaBd;
  procust: GammaBd;
}

export interface GammaBd {
  gamma_pos: CharmNeg;
  gamma_neg: CharmNeg;
}

export interface GammaVol {
  bd_gamma: { [key: string]: BdGammaValue };
  cust_gamma: { [key: string]: BdGammaValue };
  firm_gamma: { [key: string]: BdGammaValue };
  mm_gamma: { [key: string]: BdGammaValue };
  procust_gamma: { [key: string]: BdGammaValue };
}

export interface GammaHeatmapDatum extends GammaVol {
  timestamp: number; // timestamp (of the "timeslice")
  time: number; // time (on the chart)
  spot: number; // spot price
}

export interface BdGammaValue {
  vol_10m_std: number;
  vol_10m_mean: number;
  vol_10m_max: number;
  vol_10m_min: number;
  mean: number;
  max: number;
  min: number;
}

export interface RelDelta {
  bd: RelDeltaBd;
  cust: RelDeltaBd;
  firm: RelDeltaBd;
  mm: RelDeltaBd;
  procust: RelDeltaBd;
}

export interface RelDeltaBd {
  rel_delta_pos: CharmNeg;
  rel_delta_neg: CharmNeg;
}

export interface RelGamma {
  bd: RelGammaBd;
  cust: RelGammaBd;
  firm: RelGammaBd;
  mm: RelGammaBd;
  procust: RelGammaBd;
}

export interface RelGammaBd {
  rel_gamma_pos: CharmNeg;
  rel_gamma_neg: CharmNeg;
}

export interface StrikeGex {
  bd: StrikeGexBd;
  cust: StrikeGexBd;
  firm: StrikeGexBd;
  mm: StrikeGexBd;
  procust: StrikeGexBd;
}

export interface StrikeGexBd {
  gamma_pos: CharmNeg;
  gamma_neg: CharmNeg;
  gamma_0_pos: CharmNeg;
  gamma_0_neg: CharmNeg;
}

export interface StrikeOi {
  bd: { [key: string]: CharmNeg };
  cust: { [key: string]: CharmNeg };
  firm: { [key: string]: CharmNeg };
  mm: { [key: string]: CharmNeg };
  procust: { [key: string]: CharmNeg };
}

export interface EquityBySym {
  trade_date: Date;
  quote_date: Date;
  sym: string;
  name: string;
  upx: number;
  callsum: number;
  putsum: number;
  minfs: number;
  earnings_utc: null;
  keyd: number;
  largeCoi: number;
  largePoi: number;
  next_exp_call_gamma: number;
  next_exp_put_gamma: number;
  next_exp_g: number;
  next_exp_d: number;
  max_exp_g_date: Date;
  max_exp_d_date: Date;
  atmgc: number;
  atmgp: number;
  atmdc: number;
  atmdp: number;
  pv: number;
  cv: number;
  d95ne: number;
  d25ne: number;
  d95: number;
  d25: number;
  putctrl: number;
  activity_factor: number;
  position_factor: number;
  date: null;
  total_volume: null;
  stock_volume: null;
  dpi: null;
  dpi_high52w: null;
  dpi_low52w: null;
  cws: number;
  pws: number;
  keyg: number;
  maxfs: number;
  dpi_sector: null;
  dpi_volume: null;
  put_call_ratio: number;
  volume_ratio: number;
  gamma_ratio: number;
  delta_ratio: number;
  totaldelta: number;
  ne_skew: number;
  skew: number;
  cskew: number;
  pskew: number;
  options_implied_move: number;
  atm_iv30: number;
  rv30: number;
  fwd_garch: number;
  iv_pct: number;
  iv_rank: number;
  skew_rank: number;
  cskew_pct: number;
  pskew_pct: number;
  garch_rank: number;
  garch_scanner: null;
  vrp_scanner: null;
  vrp_scanner_high: null;
  squeeze_scanner: null;
  ne_call_volume: number;
  ne_put_volume: number;
  strike_list: string;
  cg_list: string;
  pg_list: string;
  mf_list: string;
  smf_list: string;
  call_strikes_list_absg: string;
  call_gnot_list_absg: string;
  put_strikes_list_absg: string;
  put_gnot_list_absg: string;
  next_exp_strike_calls_list: string;
  next_exp_gamma_call_list: string;
  next_exp_strike_puts_list: string;
  next_exp_gamma_put_list: string;
  hist_trade_dates: null;
  hist_dpi: null;
  hist_dpi_volume: null;
  hist_px: null;
  hist_high: null;
  hist_low: null;
  sig: number;
}

export interface EhSymbolsItem {
  sym: string;
  name: string;
}

export interface IntradayStrikeGEX {
  strike_price: number;
  timestamp: Date;
  bd_gamma: number;
  cust_gamma: number;
  firm_gamma: number;
  mm_gamma: number;
  procust_gamma: number;
  bd_gamma_0: number;
  cust_gamma_0: number;
  firm_gamma_0: number;
  mm_gamma_0: number;
  procust_gamma_0: number;
  is_call?: boolean;
}

interface ExpirySpotGreekRowBase {
  // the value of time for the gamma/delta being specified in the heatmap chart
  time: string;

  // the price for the stock in the projected scenario
  spot: number;

  // the actual time that data was received from CBOE.
  // (Corresponds with the "time slider" in the chart UX)
  timestamp: string;
}

export interface ExpirySpotDeltaRow extends ExpirySpotGreekRowBase {
  delta: number;
}

export interface ExpirySpotGammaRow extends ExpirySpotGreekRowBase {
  gamma: number;
}
